import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { Download } from 'lucide-react'

interface ReportData {
  spending_by_category: { category: string; amount: number }[]
  spending_by_company: { company: string; amount: number }[]
  monthly_trend: { month: string; amount: number }[]
  top_merchants: { merchant: string; count: number; amount: number }[]
}

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

const formatCurrency = (pence: number) => 
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)

export default function Reports() {
  const { data: report, isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: () => api.get<ReportData>('/reports/summary')
  })

  const exportCSV = async () => {
    const res = await api.get<{ csv: string }>('/reports/export')
    const blob = new Blob([res.csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `heysalad-report-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  if (isLoading) return <div className="animate-pulse">Loading reports...</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spending by Category */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Spending by Category</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={report?.spending_by_category || []}
                  dataKey="amount"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                >
                  {(report?.spending_by_category || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Spending by Company */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Spending by Company</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report?.spending_by_company || []} layout="vertical">
                <XAxis type="number" tickFormatter={(v) => `£${v/100}`} />
                <YAxis type="category" dataKey="company" width={100} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="amount" fill="#22c55e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="bg-white rounded-xl p-6 shadow-sm lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">Monthly Spending Trend</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report?.monthly_trend || []}>
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `£${v/100}`} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="amount" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Merchants */}
        <div className="bg-white rounded-xl p-6 shadow-sm lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">Top Merchants</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Merchant</th>
                  <th className="text-right py-2 text-sm font-medium text-gray-500">Transactions</th>
                  <th className="text-right py-2 text-sm font-medium text-gray-500">Total Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(report?.top_merchants || []).map((m, i) => (
                  <tr key={i}>
                    <td className="py-3 font-medium">{m.merchant}</td>
                    <td className="py-3 text-right text-gray-600">{m.count}</td>
                    <td className="py-3 text-right font-medium text-salad-600">{formatCurrency(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
